import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { PeriodMonthColumn } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager-month';
import TooltipRateGroupsMonth from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-rate-groups-month';
import type { Period } from 'lumen/models';
import type { RateGroup } from 'lumen/models/pa-subscription-meta-info';
import { formatMinLosValue } from 'lumen/utils/pricing-assistant/restriction-display-utils';

/* The hotel level's column also carries the configured rate groups, whose MinLOS
   its tooltip lists under the hotel's; without any, the tooltip has the hotel row
   only. Rooms have no tooltip. */
export interface RestrictionsMonthColumn extends PeriodMonthColumn {
  rateGroups?: RateGroup[];
}

interface HotelPricingAssistantTableCellsTdRestrictionsMonthSignature {
  Args: {
    column: RestrictionsMonthColumn;
    value: Period;
  };
}

export default class HotelPricingAssistantTableCellsTdRestrictionsMonth extends Component<HotelPricingAssistantTableCellsTdRestrictionsMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  get displayValue(): string {
    // formatMinLosValue spells 0 out as "No MinLOS restriction"; the table wants "Open"
    // TODO: switch to a shared general.restriction.none key once it exists in Lokalise
    if (this.minLos === 0) {
      return this.intl.t('channel_pricing.restriction.none');
    }

    return formatMinLosValue({ intl: this.intl, value: this.minLos });
  }

  get hasTooltip(): boolean {
    return !this.isRoom && !this.isMissing;
  }

  /* The MinLOS needs both restrictions requests, as the Rate groups cell does.
     The BE recalculates the hotel MinLOS from the rate groups, so it is stale
     while any of them is pushing. Rooms have no pushes yet. */
  get isLoading(): boolean {
    const { restrictionsRecommendationsRequestTask, restrictionsRequestTask } =
      this.pricingAssistantDataRequestService;

    return (
      restrictionsRequestTask.isRunning ||
      restrictionsRecommendationsRequestTask.isRunning ||
      (!this.isRoom && this.args.value.hasRateGroupMinLosActionInProgress)
    );
  }

  get isMissing(): boolean {
    return this.minLos == null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get isRoom(): boolean {
    return this.args.column.pmsRoomId !== undefined;
  }

  get minLos(): number | null {
    const { pmsRoomId } = this.args.column;

    return pmsRoomId !== undefined
      ? this.args.value.roomMinLos(pmsRoomId)
      : this.args.value.effectiveHotelMinLosWithOverride;
  }

  get rateGroups(): RateGroup[] {
    return this.args.column.rateGroups ?? [];
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      {{! Focusable so keyboard users can open the tooltip }}
      <div
        class="mr-500
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id={{if
          this.isRoom
          "td-room-min-los-month"
          "td-hotel-min-los-month"
        }}
        tabindex={{if this.hasTooltip "0"}}
      >
        {{this.displayValue}}
        {{#if this.hasTooltip}}
          <TooltipRateGroupsMonth
            @hotelMinLos={{this.minLos}}
            @period={{@value}}
            @rateGroups={{this.rateGroups}}
          />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
